import Column from 'components/Column'
import { ConfirmModalState } from 'components/ConfirmSwapModal'
import { Swap } from 'components/Icons/Swap'
import { SwapResult } from 'hooks/useSwapCallback'
import { t } from 'i18n'
import { useMemo } from 'react'
import { useSwapTransactionStatus } from 'state/transactions/hooks'
import styled, { useTheme } from 'styled-components'
import { colors } from 'theme/colors'
import { Divider } from 'theme/components'
import { TransactionStatus } from 'uniswap/src/data/graphql/uniswap-data-api/__generated__/types-and-hooks'
import { ReactComponent as NutcoinIcon } from '../../assets/svg/nutcoin-logo.svg'
import { Step, StepDetails, StepStatus } from './Step'
import { Currency } from '@uniswap/sdk-core'
import { useSrcColor } from 'hooks/useColor'

const DividerContainer = styled(Column)`
  height: 28px;
  padding: 0px 16px;
  justify-content: center;
`
const Edge = styled.div`
  width: 2px;
  height: 10px;
  background-color: ${({ theme }) => theme.neutral3};
  margin: 0px 27px;
`

type ProgressIndicatorStep = Extract<
  ConfirmModalState,
  | ConfirmModalState.APPROVING_TOKEN  
  | ConfirmModalState.PERMITTING
  | ConfirmModalState.PENDING_CONFIRMATION
  | ConfirmModalState.WRAPPING
  | ConfirmModalState.RESETTING_TOKEN_ALLOWANCE
>

export default function ProgressIndicator({
  steps,
  currentStep,
  swapResult,
  inputCurrency,
  tokenApprovalPending = false,
  revocationPending = false,
}: {
  steps: ProgressIndicatorStep[]
  currentStep: ProgressIndicatorStep
  inputCurrency?: Currency
  swapResult?: SwapResult
  tokenApprovalPending?: boolean
  revocationPending?: boolean
}) {
  const theme = useTheme()
  const inputTokenColor = useSrcColor("https://i.postimg.cc/MpM4wSzK/icon-2-100x100.png", inputCurrency?.name).tokenColor ?? theme.accent1

  const swapStatus = useSwapTransactionStatus(swapResult)
  const swapConfirmed = swapStatus === TransactionStatus.Confirmed
  const swapPending = swapResult !== undefined && !swapConfirmed
  const transactionPending = revocationPending || tokenApprovalPending || swapPending

  
  function getStatus(targetStep: ProgressIndicatorStep) {
    const currentIndex = steps.indexOf(currentStep)
    const targetIndex = steps.indexOf(targetStep)

    if (currentIndex < targetIndex) {
      return StepStatus.PREVIEW
    } else if (currentIndex === targetIndex) {
      return transactionPending ? StepStatus.IN_PROGRESS : StepStatus.ACTIVE
    } else {
      return StepStatus.COMPLETE
    }
  }


  const stepDetails: Record<ProgressIndicatorStep, StepDetails> = useMemo(
    () => ({
      [ConfirmModalState.WRAPPING]: {
        // NORMALLY NEVER HERE
        icon: <div></div>,
        rippleColor: inputTokenColor,
        previewTitle: t(``),
        actionRequiredTitle: t``,
        inProgressTitle: t``,
      },
      [ConfirmModalState.RESETTING_TOKEN_ALLOWANCE]: {
        icon: <NutcoinIcon height={"24px"} width={"24px"}  />,
        rippleColor: inputTokenColor,
        previewTitle: t(`Reset {{symbol}} limit`, { symbol: inputCurrency?.symbol }),
        actionRequiredTitle: t(`Reset {{symbol}} limit in wallet`, { symbol: inputCurrency?.symbol }),
        inProgressTitle: t(`Resetting {{symbol}} limit...`, { symbol: inputCurrency?.symbol }),
      },
      [ConfirmModalState.APPROVING_TOKEN]: {
        icon: <NutcoinIcon height={"24px"} width={"24px"}  />,
        rippleColor: inputTokenColor,
        previewTitle: t(`Approve {{symbol}} spending`, { symbol: inputCurrency?.symbol}),
        actionRequiredTitle: t`Approve in wallet`,
        inProgressTitle: t`Approval pending...`,
      },
      [ConfirmModalState.PERMITTING]: {
        // NORMALLY NEVER HERE
        icon: <div></div>,
        rippleColor: inputTokenColor,
        previewTitle: t(``),
        actionRequiredTitle: t``,
        inProgressTitle: t``,
      },
      [ConfirmModalState.PENDING_CONFIRMATION]: {
        icon: <Swap />,
        rippleColor: colors.blue400,
        previewTitle: t`Confirm mint`,
        actionRequiredTitle: t`Confirm mint in wallet`,
        inProgressTitle: t`Mint pending...`,
      },
    }),
    [inputTokenColor, theme.accent1]
  )

  if (steps.length === 0) {
    return null
  }

  return (
    <Column>
      <DividerContainer>
        <Divider />
      </DividerContainer>
      {steps.map((step, i) => {
        return (
          <div key={`progress-indicator-step-${i}`}>
            <Step stepStatus={getStatus(step)} stepDetails={stepDetails[step]} />
            {i !== steps.length - 1 && <Edge />}
          </div>
        )
      })}
    </Column>
  )
}
